import React from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { useTheme } from '@mui/material/styles';
import { getDisplayedStepStatus } from '../utils/processDisplay';

const ProcessStatusTracker = ({ process, stepsArray, isMobile }) => {
    const theme = useTheme();

    return (
        <Box sx={{ 
            mt: 3,
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
            gap: 1
        }}>
            {Object.entries(process.steps || {}).map(([stepName, stepData], index) => {
                const displayedStatus = getDisplayedStepStatus(process.status, stepData.status);

                return (
                <Box
                    key={stepName}
                    sx={{
                        p: 1,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'background.paper',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        position: 'relative',
                        transition: 'all 0.2s ease-in-out',
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider,
                        '&:hover': {
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.02)'
                        }
                    }}
                >
                    {displayedStatus === 'finished' ? (
                        <CheckCircleIcon 
                            color="success" 
                            sx={{ fontSize: 20 }} 
                        />
                    ) : displayedStatus === 'error' ? (
                        <ErrorIcon 
                            color="error" 
                            sx={{ fontSize: 20 }} 
                        />
                    ) : displayedStatus === 'skipped' ? (
                        <RemoveCircleOutlineIcon 
                            sx={{ 
                                fontSize: 20,
                                color: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)'
                            }} 
                        />
                    ) : displayedStatus === 'started' ? (
                        stepName === 'upload_audio' && process.progress && process.progress[stepName] ? (
                            <Box sx={{ position: 'relative', display: 'inline-flex', width: 24, height: 24 }}>
                                <CircularProgress 
                                    size={24} 
                                    thickness={4}
                                    variant="determinate" 
                                    value={process.progress[stepName]}
                                />
                                <Box
                                    sx={{
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        right: 0,
                                        position: 'absolute',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 'bold' }}>
                                        {process.progress[stepName]}%
                                    </Typography>
                                </Box>
                            </Box>
                        ) : (
                            <CircularProgress 
                                size={20} 
                                thickness={4}
                            />
                        )
                    ) : (
                        <RadioButtonUncheckedIcon 
                            color="disabled" 
                            sx={{ fontSize: 20 }} 
                        />
                    )}
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            fontWeight: 500,
                            color: displayedStatus === 'error' ? 'error.main' :
                                   displayedStatus === 'skipped' ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.38)') :
                                   'text.primary',
                            fontSize: '0.875rem',
                            textDecoration: displayedStatus === 'skipped' ? 'line-through' : 'none'
                        }}
                    >
                        {stepName === 'completed' ? 'Completed' : (stepsArray[index] || stepName)}
                    </Typography>
                </Box>
                );
            })}
        </Box>
    );
};

export default ProcessStatusTracker;
